import { mockResponseForPost } from "@/modules/api/globalApiClient";
import { logger } from "@/modules/logger";
import { toastModule } from "@/modules/toast";
import { CustomSyncErrorKind } from "@/store/sync/operations/errors/SyncError";
import { SyncOperationKind } from "@/store/sync/types";
import { PublicAppSubStoreArgs, PublicAppSubStore } from "@/store/types";
import { action, makeObservable, observable, reaction, runInAction } from "mobx";
import localforage from "localforage";
import Dexie from "dexie";

enum LocalStorageKey {
  DebugModeEnabled = "debugModeEnabled",
  DebugModeToolbarExpanded = "debugModeToolbarExpanded",
  DebugOverlayView = "debugOverlayView",
  ForceOnboardingMode = "forceOnboardingMode",
}

export enum DebugOverlayViewKind {
  Standard = "standard",
  Database = "database",
  Account = "account",
  FeatureFlags = "feature-flags",
  HeadsUp = "heads-up",
  Memory = "memory",
  Electron = "electron",
  WorkingContext = "working-context",
  Cache = "cache",
  Onboarding = "onboarding",
  Search = "search",
  Monetization = "monetization",
}

export class AppStoreDebugStore extends PublicAppSubStore {
  debugModeEnabled = false;
  debugModeToolbarExpanded = true;
  debugOverlayView: DebugOverlayViewKind = DebugOverlayViewKind.Standard;
  forceOnboardingMode = false;
  operationsSubmitApiMockErrorKind?: CustomSyncErrorKind;
  operationsSubmitApiMockErrorOperationKind?: SyncOperationKind;
  latestSmartSearchResponse: unknown = null;
  latestFastSearchResponse: unknown = null;
  latestSearchQuery: string | null = null;

  constructor(injectedDeps: PublicAppSubStoreArgs) {
    super(injectedDeps);

    makeObservable(this, {
      debugModeEnabled: observable,
      debugModeToolbarExpanded: observable,
      debugOverlayView: observable,
      forceOnboardingMode: observable,
      operationsSubmitApiMockErrorKind: observable,
      operationsSubmitApiMockErrorOperationKind: observable,
      setOperationsSubmitApiMockErrorKind: action,
      setOperationsSubmitApiMockErrorOperationKind: action,
      toggleDebugMode: action,
      toggleDebugModeToolbar: action,
      setDebugOverlayView: action,
      toggleForceOnboardingMode: action,
      initialize: action,
      resetState: action,
      latestSmartSearchResponse: observable,
      latestFastSearchResponse: observable,
      latestSearchQuery: observable,
      setLatestSmartSearchResponse: action,
      setLatestFastSearchResponse: action,
      setLatestSearchQuery: action,
    });

    reaction(
      () => ({
        operationsSubmitApiMockErrorKind: this.operationsSubmitApiMockErrorKind,
        operationsSubmitApiMockErrorOperationKind: this.operationsSubmitApiMockErrorOperationKind,
      }),
      ({ operationsSubmitApiMockErrorKind, operationsSubmitApiMockErrorOperationKind }) => {
        const OPERATIONS_SUBMIT_URL = "/v2/sync/operations/submit";
        if (!operationsSubmitApiMockErrorKind) {
          mockResponseForPost(OPERATIONS_SUBMIT_URL);
          return;
        }
        mockResponseForPost(OPERATIONS_SUBMIT_URL, operationsSubmitApiMockErrorOperationKind, {
          data: {
            status: "FAILED",
            info: {
              error_category: "CUSTOM_ERROR",
              error_data: {
                kind: operationsSubmitApiMockErrorKind,
                value: {},
              },
            },
          },
        });
      }
    );
  }

  setOperationsSubmitApiMockErrorKind = (kind?: CustomSyncErrorKind) => {
    this.operationsSubmitApiMockErrorKind = kind;
  };

  setOperationsSubmitApiMockErrorOperationKind = (kind?: SyncOperationKind) => {
    this.operationsSubmitApiMockErrorOperationKind = kind;
  };

  setLatestSmartSearchResponse = (response: unknown) => {
    this.latestSmartSearchResponse = response;
  };

  setLatestFastSearchResponse = (response: unknown) => {
    this.latestFastSearchResponse = response;
  };

  setLatestSearchQuery = (query: string) => {
    this.latestSearchQuery = query;
  };

  toggleDebugMode = async () => {
    const nextValue = !this.debugModeEnabled;

    logger.info({
      message: `[AppStoreDebugStore] [toggleDebugMode] { value: ${nextValue} }`,
    });

    toastModule.triggerToast({
      content: `Debug Mode ${nextValue ? "Enabled" : "Disabled"}`,
    });

    runInAction(() => {
      this.debugModeEnabled = nextValue;
    });

    if (nextValue) {
      this.debugModeToolbarExpanded = true;
    }

    Dexie.debug = nextValue;

    await localforage.setItem(LocalStorageKey.DebugModeEnabled, nextValue);
  };

  toggleDebugModeToolbar = async () => {
    const nextValue = !this.debugModeToolbarExpanded;

    runInAction(() => {
      this.debugModeToolbarExpanded = nextValue;
    });

    await localforage.setItem(LocalStorageKey.DebugModeToolbarExpanded, nextValue);
  };

  setDebugOverlayView = (view: DebugOverlayViewKind) => {
    this.debugOverlayView = view;
    void localforage.setItem(LocalStorageKey.DebugOverlayView, view);
  };

  toggleForceOnboardingMode = async () => {
    const nextValue = !this.forceOnboardingMode;

    runInAction(() => {
      this.forceOnboardingMode = nextValue;
    });

    await localforage.setItem(LocalStorageKey.ForceOnboardingMode, nextValue);
  };

  initialize = async () => {
    // Load persisted values
    const [
      persistedDebugModeEnabled,
      persistedDebugModeToolbarExpanded,
      persistedDebugOverlayView,
      persistedForceOnboardingMode,
    ] = await Promise.all([
      localforage.getItem<boolean>(LocalStorageKey.DebugModeEnabled),
      localforage.getItem<boolean>(LocalStorageKey.DebugModeToolbarExpanded),
      localforage.getItem<DebugOverlayViewKind>(LocalStorageKey.DebugOverlayView),
      localforage.getItem<boolean>(LocalStorageKey.ForceOnboardingMode),
    ]);

    runInAction(() => {
      this.debugModeEnabled = persistedDebugModeEnabled ?? false;
      this.debugModeToolbarExpanded = persistedDebugModeToolbarExpanded ?? false;
      this.forceOnboardingMode = persistedForceOnboardingMode ?? false;
      if (
        persistedDebugOverlayView &&
        (Object.values(DebugOverlayViewKind) as string[]).includes(persistedDebugOverlayView)
      ) {
        this.debugOverlayView = persistedDebugOverlayView as DebugOverlayViewKind;
      }
    });
  };

  resetState = async () => {
    runInAction(() => {
      this.debugModeEnabled = false;
      this.debugModeToolbarExpanded = false;
      this.forceOnboardingMode = false;
    });

    await Promise.all([
      localforage.removeItem(LocalStorageKey.DebugModeEnabled),
      localforage.removeItem(LocalStorageKey.DebugModeToolbarExpanded),
      localforage.removeItem(LocalStorageKey.DebugOverlayView),
      localforage.removeItem(LocalStorageKey.ForceOnboardingMode),
    ]);
  };
}
